import { useEffect, useRef, useState } from "react";
import { Check, CircleCheck, CircleX, Download, X } from "lucide-react";
import { Badge, Button, Card, Label, Textarea } from "@wa/ui";
import type { EstimateDecision } from "@wa/core/estimate-answer";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { portal, pdfHref, type PortalEstimateDetail } from "../../../lib/portal";
import { fmtDate, fmtDateTime, fmtAmount, genericStatusVariant } from "../../../lib/format";
import { DocLayout, DocSkeleton, Figure, LineItems, Notice, PageHead, Totals } from "../../ui";
import { shownStatus } from "./lib";

/**
 * One quote, as a page: what we offered on the left, the client's answer on
 * the right.
 *
 * `answerable` comes from the server and is never re-derived here, so the two
 * buttons appear exactly when the API will take the answer.
 *
 * Declining takes two presses. The first opens a box for a reason and swaps the
 * buttons; only the second sends. The irreversible half of this screen is never
 * one click away from a page the client only meant to read.
 */
export function EstimatePage({ id, onBack }: { id: string; onBack: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [detail, setDetail] = useState<PortalEstimateDetail | null>(null);
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    let live = true;
    // An id this reader may not open answers 404; the list is the answer.
    void portal.estimate(id).then((d) => { if (live) setDetail(d); }).catch(() => { if (live) onBack(); });
    return () => {
      live = false;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // On a phone the Decline button sits in the bar at the bottom while the
  // reason box is up in the page. Bring the box to the reader instead of
  // leaving them to look for what their press just opened.
  useEffect(() => {
    if (!declining) return;
    noteRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    noteRef.current?.focus({ preventScroll: true });
  }, [declining]);

  async function answer(decision: EstimateDecision) {
    if (!detail || busy) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await portal.answerEstimate(
        detail.id,
        decision,
        decision === "declined" ? note.trim() : undefined,
      );
      setDetail(updated);
      setDeclining(false);
    } catch {
      // The one refusal worth wording separately is a quote that stopped being
      // answerable while this page was open - somebody at the desk marked it,
      // or its last valid day went by. Reading it again is what makes the
      // screen agree with the server.
      setError(t("portal.estimates.answerFailed"));
      void portal.estimate(detail.id).then(setDetail).catch(() => {});
    } finally {
      setBusy(false);
    }
  }

  if (!detail) return <DocSkeleton label={t("common.loading")} />;

  const money = (cents: number) => fmtAmount(cents, detail.currency, locale);
  const pdf = pdfHref(`/portal/estimates/${detail.id}/pdf`);
  const decision = detail.portalDecision;
  const status = shownStatus(detail);

  const acceptButton = (className?: string) => (
    <Button size="lg" loading={busy} onClick={() => void answer("accepted")} className={className}>
      <Check />
      {t("portal.estimates.accept")}
    </Button>
  );
  const declineButton = (className?: string) => (
    <Button size="lg" variant="outline" disabled={busy} onClick={() => setDeclining(true)} className={className}>
      <X />
      {t("portal.estimates.decline")}
    </Button>
  );
  const confirmDecline = (className?: string) => (
    <Button size="lg" variant="destructive" loading={busy} onClick={() => void answer("declined")} className={className}>
      <X />
      {t("portal.estimates.declineConfirm")}
    </Button>
  );
  const cancelDecline = (className?: string) => (
    <Button
      size="lg"
      variant="outline"
      disabled={busy}
      onClick={() => {
        setDeclining(false);
        setNote("");
      }}
      className={className}
    >
      {t("common.cancel")}
    </Button>
  );
  const pdfButton = (className?: string) => (
    <Button asChild size="lg" variant="outline" className={className}>
      <a href={pdf} target="_blank" rel="noreferrer">
        <Download />
        {t("portal.invoices.downloadPdf")}
      </a>
    </Button>
  );

  return (
    <div>
      <PageHead
        back={{ label: t("portal.estimates.back"), onClick: onBack }}
        title={`${t("portal.estimates.one")} ${detail.number}`}
        badge={
          detail.answerable ? (
            <Badge>{t("portal.estimates.needsAnswer")}</Badge>
          ) : (
            <Badge variant={genericStatusVariant(status)}>{t(`portal.estimateStatus.${status}`)}</Badge>
          )
        }
        description={[detail.title, detail.client.name].filter(Boolean).join(" · ")}
      />

      <DocLayout
        paper={
          <Card className="p-5 md:p-8">
            <dl className="flex flex-wrap gap-x-12 gap-y-4">
              <Fact label={t("portal.estimates.created")} value={fmtDate(detail.createdAt, locale)} />
              <Fact label={t("portal.estimates.validUntil")} value={fmtDate(detail.validUntil, locale)} />
              <Fact label={t("portal.client")} value={detail.client.name} />
            </dl>

            <div className="mt-8">
              <LineItems lines={detail.lineItems} currency={detail.currency} />
            </div>

            <Totals
              className="mt-6"
              rows={[
                { label: t("portal.invoices.subtotal"), value: money(detail.subtotalCents) },
                // Left off a document that charges no VAT - see `showVat` on the
                // detail type. An absent flag means yes.
                ...(detail.showVat !== false
                  ? [{ label: t("portal.invoices.tax"), value: money(detail.taxCents) }]
                  : []),
                { label: t("portal.invoices.grandTotal"), value: money(detail.totalCents), strong: true },
              ]}
            />

            {detail.notes && (
              <div className="mt-8 border-t border-border pt-6">
                <h2 className="text-sm font-bold">{t("portal.notes")}</h2>
                <p className="mt-1.5 max-w-prose whitespace-pre-wrap text-sm text-foreground/70">{detail.notes}</p>
              </div>
            )}
          </Card>
        }
        lead={
          <Card className="p-6">
            <Figure
              size="lg"
              label={t("portal.invoices.grandTotal")}
              value={money(detail.totalCents)}
              hint={detail.validUntil && `${t("portal.estimates.validUntil")} ${fmtDate(detail.validUntil, locale)}`}
            />

            {/* What the client already answered, if they answered it here. */}
            {decision && (
              <Notice
                icon={decision.decision === "accepted" ? CircleCheck : CircleX}
                tone={decision.decision === "accepted" ? "success" : "neutral"}
                className="mt-5"
              >
                <p className="font-semibold">
                  {t(`portal.estimates.answered.${decision.decision}`, { name: decision.name ?? "" })}
                </p>
                {decision.at && <p className="text-xs text-foreground/70">{fmtDateTime(decision.at, locale)}</p>}
                {decision.note && <p className="mt-2 whitespace-pre-wrap text-foreground/70">{decision.note}</p>}
              </Notice>
            )}

            {declining && (
              <div className="mt-5 space-y-1.5">
                <Label htmlFor="estimate-decline-note">{t("portal.estimates.declineReason")}</Label>
                <Textarea
                  ref={noteRef}
                  id="estimate-decline-note"
                  rows={3}
                  maxLength={2000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("portal.estimates.declineReasonHint")}
                />
              </div>
            )}

            {error && <p role="alert" className="mt-4 text-sm text-destructive">{error}</p>}

            {/* Under `lg` the answer buttons sit in the bar at the bottom of
                the screen, so they are not drawn twice. The PDF stays here at
                every width: on a phone the bar is for the answer alone. */}
            <div className="mt-5 flex flex-col gap-2.5">
              {detail.answerable && !declining && (
                <>
                  {acceptButton("max-lg:hidden")}
                  {declineButton("max-lg:hidden")}
                </>
              )}
              {detail.answerable && declining && (
                <>
                  {confirmDecline("max-lg:hidden")}
                  {cancelDecline("max-lg:hidden")}
                </>
              )}
              {pdfButton()}
            </div>
          </Card>
        }
        phoneBar={
          !detail.answerable ? undefined : declining ? (
            <>
              {cancelDecline("px-4")}
              {confirmDecline("min-w-0 flex-1 px-4")}
            </>
          ) : (
            <>
              {declineButton("px-4")}
              {acceptButton("min-w-0 flex-1 px-4")}
            </>
          )
        }
      />
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold uppercase tracking-wider text-foreground/70">{label}</dt>
      <dd className="mt-1 font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
